import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api } from "@/lib/api";
import { formatCents } from "@/lib/format";
import type { BeneficioCatalogo } from "@/data/beneficios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

type Acao = "incluir" | "alterar" | "remover";

interface LinhaRecalculo {
  colaboradorId: string;
  nome: string;
  vinculo: string;
  acao: Acao;
  /** Centavos por benefício. */
  de: Record<string, number>;
  para: Record<string, number>;
  totalDe: number;
  totalPara: number;
}

interface Resultado {
  confirmado: boolean;
  linhas: LinhaRecalculo[];
}

const ACAO: Record<Acao, { rotulo: string; variant: "default" | "secondary" | "destructive" }> = {
  incluir: { rotulo: "Entra", variant: "default" },
  alterar: { rotulo: "Muda", variant: "secondary" },
  remover: { rotulo: "Sai", variant: "destructive" },
};

interface Props {
  open: boolean;
  onClose: () => void;
  folhaId: string;
  catalogo: BeneficioCatalogo[];
  /** Folha já exportada ou lançada no ERP: o recálculo não refaz esses envios. */
  jaEnviada: boolean;
  onSalvo: () => void;
}

/**
 * Refaz a folha pelo cadastro de hoje. Mostra antes quem entra, sai ou muda;
 * o RH desmarca quem tem ajuste feito só nesta folha e quer manter.
 */
export const RecalcularBeneficiosModal = ({ open, onClose, folhaId, catalogo, jaEnviada, onSalvo }: Props) => {
  const [previa, setPrevia] = useState<LinhaRecalculo[] | null>(null);
  const [marcados, setMarcados] = useState<Set<string>>(new Set());
  const [carregando, setCarregando] = useState(false);

  useEffect(() => {
    if (!open) {
      setPrevia(null);
      return;
    }
    setCarregando(true);
    api
      .post<Resultado>(`/beneficios/folhas/${folhaId}/recalcular`, { confirmar: false })
      .then((r) => {
        setPrevia(r.linhas);
        setMarcados(new Set(r.linhas.map((l) => l.colaboradorId)));
      })
      .catch((err) => {
        toast.error(err instanceof Error ? err.message : "Erro ao recalcular");
        onClose();
      })
      .finally(() => setCarregando(false));
    // onClose fica de fora: o pai passa inline e refaria a prévia a cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, folhaId]);

  const nomeDe = (tipo: string) => catalogo.find((b) => b.tipo === tipo)?.nome ?? "Benefício";

  /** "VR R$ 500,00 → R$ 640,00 · VA novo" — só os benefícios que mudam. */
  const detalhe = (l: LinhaRecalculo) =>
    [...new Set([...Object.keys(l.de), ...Object.keys(l.para)])]
      .filter((t) => l.de[t] !== l.para[t])
      .map((t) =>
        l.de[t] && l.para[t]
          ? `${nomeDe(t)} ${formatCents(l.de[t])} → ${formatCents(l.para[t])}`
          : l.para[t]
            ? `${nomeDe(t)} ${formatCents(l.para[t])}`
            : `${nomeDe(t)} sai`,
      )
      .join(" · ");

  const alternar = (id: string, v: boolean) =>
    setMarcados((atual) => {
      const novo = new Set(atual);
      if (v) novo.add(id);
      else novo.delete(id);
      return novo;
    });

  const aplicar = async () => {
    setCarregando(true);
    try {
      const r = await api.post<Resultado>(`/beneficios/folhas/${folhaId}/recalcular`, {
        confirmar: true,
        colaboradorIds: [...marcados],
      });
      toast.success(`${r.linhas.length} colaborador(es) recalculado(s)`);
      onSalvo();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao recalcular");
    } finally {
      setCarregando(false);
    }
  };

  const contagem = (a: Acao) => previa?.filter((l) => l.acao === a).length ?? 0;
  const todos = !!previa && previa.length > 0 && marcados.size === previa.length;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Recalcular folha de benefícios</DialogTitle>
          <DialogDescription>
            Refaz a folha pelo cadastro de hoje: entra quem foi cadastrado depois, sai quem foi desativado e muda quem
            teve o valor próprio, o fixo do vínculo ou o complemento de salário alterado. Ajustes feitos só nesta folha
            são substituídos — desmarque quem quiser manter como está.
          </DialogDescription>
        </DialogHeader>

        {jaEnviada && (
          <p className="rounded-md border border-amber-500/35 bg-amber-500/[0.08] px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
            Esta folha já foi exportada ou lançada no ERP. O recálculo não refaz esses envios: exporte de novo e confira
            as contas no ERP depois.
          </p>
        )}

        {carregando && !previa ? (
          <Loader2 className="mx-auto my-8 h-5 w-5 animate-spin text-muted-foreground" />
        ) : previa && previa.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            A folha já está de acordo com o cadastro. Nada a recalcular.
          </p>
        ) : previa ? (
          <>
            <div className="flex flex-wrap gap-2 text-xs">
              {contagem("incluir") > 0 && <Badge variant="default">{contagem("incluir")} entram</Badge>}
              {contagem("alterar") > 0 && <Badge variant="secondary">{contagem("alterar")} mudam</Badge>}
              {contagem("remover") > 0 && <Badge variant="destructive">{contagem("remover")} saem</Badge>}
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={todos}
                      onCheckedChange={(v) =>
                        setMarcados(v === true ? new Set(previa.map((l) => l.colaboradorId)) : new Set())
                      }
                      aria-label="Marcar todos"
                    />
                  </TableHead>
                  <TableHead>Colaborador</TableHead>
                  <TableHead className="w-20" />
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {previa.map((l) => (
                  <TableRow key={l.colaboradorId}>
                    <TableCell>
                      <Checkbox
                        checked={marcados.has(l.colaboradorId)}
                        onCheckedChange={(v) => alternar(l.colaboradorId, v === true)}
                        aria-label={`Recalcular ${l.nome}`}
                      />
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">{l.nome}</span>
                      <span className="ml-1.5 text-[10px] font-bold uppercase text-muted-foreground">
                        {l.vinculo === "clt" ? "CLT" : "PJ"}
                      </span>
                      <span className="block text-xs text-muted-foreground">{detalhe(l)}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={ACAO[l.acao].variant}>{ACAO[l.acao].rotulo}</Badge>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right text-sm tabular-nums">
                      {l.acao === "incluir" ? "" : `${formatCents(l.totalDe)} → `}
                      <strong>{formatCents(l.totalPara)}</strong>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={carregando}>
            Cancelar
          </Button>
          <Button onClick={() => void aplicar()} disabled={carregando || marcados.size === 0}>
            {carregando && previa && <Loader2 className="animate-spin" />}
            Recalcular {marcados.size} colaborador{marcados.size === 1 ? "" : "es"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
